/**
 * Segment import runs history + removals audit viewer (code-audit M7).
 * Lists the recent import runs for a segment; committed replace runs expand
 * into a server-paginated removals audit (PostgREST caps unbounded selects
 * at 1000, so the audit always pages with an exact total).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getImportRuns,
  getImportRunRemovals,
  type ImportRunSummary,
} from "../../services/segmentReplaceImport.service";

const PAGE_SIZE = 100;

function RemovalsAudit({ runId }: { runId: string }) {
  const [page, setPage] = useState(0);
  const { data, isLoading } = useQuery({
    queryKey: ["segment-import-removals", runId, page],
    queryFn: () => getImportRunRemovals(runId, page, PAGE_SIZE),
  });
  if (isLoading) {
    return <p className="text-xs text-gray-500 py-2">Loading removals…</p>;
  }
  if (!data || data.total === 0) {
    return <p className="text-xs text-gray-500 py-2">No members removed.</p>;
  }
  const pages = Math.ceil(data.total / PAGE_SIZE);
  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <p className="text-xs text-gray-600 mb-1">
        {data.total.toLocaleString()} member{data.total === 1 ? "" : "s"}{" "}
        removed by this sync:
      </p>
      <ul className="text-xs text-gray-700 max-h-40 overflow-y-auto space-y-0.5">
        {data.rows.map((r) => (
          <li key={r.contact_id}>{r.email_at_removal ?? "(no email)"}</li>
        ))}
      </ul>
      {pages > 1 && (
        <div className="flex items-center gap-2 mt-2 text-xs">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="px-2 py-0.5 border rounded disabled:opacity-40"
          >
            Prev
          </button>
          <span className="text-gray-500">
            Page {page + 1} of {pages}
          </span>
          <button
            disabled={page + 1 >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="px-2 py-0.5 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

function RunRow({ run }: { run: ImportRunSummary }) {
  const [expanded, setExpanded] = useState(false);
  const counts = run.result;
  const statusColor =
    run.status === "committed"
      ? "text-green-700 bg-green-50"
      : run.status === "staging" || run.status === "previewed"
        ? "text-amber-700 bg-amber-50"
        : "text-gray-600 bg-gray-100";
  return (
    <li className="border border-gray-200 rounded-lg p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span
            className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium mr-2 ${statusColor}`}
          >
            {run.status}
          </span>
          <span className="font-medium text-gray-900">
            {run.mode === "replace" ? "Sync (replace)" : "Append"}
          </span>
          {run.filename && (
            <span className="text-gray-500 truncate"> — {run.filename}</span>
          )}
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {new Date(run.created_at).toLocaleString()}
        </span>
      </div>
      {counts && (
        <p className="mt-1 text-xs text-gray-600">
          +{counts.added_members.toLocaleString()} added
          {counts.new_contacts > 0 &&
            ` (${counts.new_contacts.toLocaleString()} new contacts)`}
          {" · "}
          <button
            onClick={() => setExpanded((e) => !e)}
            className="text-red-700 underline decoration-dotted"
          >
            −{counts.actual_removed.toLocaleString()} removed
          </button>
          {" · "}
          {counts.members_before.toLocaleString()} →{" "}
          {counts.members_after.toLocaleString()} members
        </p>
      )}
      {expanded && <RemovalsAudit runId={run.id} />}
    </li>
  );
}

export default function SegmentImportHistory({ listId }: { listId: string }) {
  const { data: runs, isLoading } = useQuery({
    queryKey: ["segment-import-runs", listId],
    queryFn: () => getImportRuns(listId),
    // History must reflect a commit made seconds ago: the app-wide 5-minute
    // staleTime would otherwise serve the pre-commit (empty) cache on remount.
    staleTime: 0,
    refetchOnMount: "always",
  });
  if (isLoading || !runs || runs.length === 0) {
    return null; // no imports yet — keep the panel quiet
  }
  return (
    <div className="mt-4">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Import History
      </h4>
      <ul className="space-y-2">
        {runs.map((r) => (
          <RunRow key={r.id} run={r} />
        ))}
      </ul>
    </div>
  );
}
